import { validatorFactoryArray } from '@laam/lib/validator';
import { useQuery } from '@tanstack/vue-query';
import {
	dropsResponseSchema,
	type DropsResponse,
	type DynamicInputData,
	type StaticInputData,
} from '@laam/cms-shared';

const validator = validatorFactoryArray<DropsResponse>(dropsResponseSchema);

const fetchDrops = async (
	filters: DynamicInputData | StaticInputData,
): Promise<DropsResponse> => {
	let body: unknown = {};
	if ('items' in filters) {
		if (filters.items.length === 0) return [];
		body = {
			id: filters.items.map((item) => item.id).join(','),
			sort_by: 'NO_RANK',
		};
	} else if ('ids' in filters) {
		if (filters.ids.length === 0) return [];
		body = {
			id: filters.ids.join(','),
			sort_by: 'NO_RANK',
		};
	} else {
		const {
			selectedPin,
			selectedCategory,
			ranking,
			dateRange,
			timePeriod,
			...rest
		} = filters;
		body = {
			...rest,
			sort_by: ranking,
			...(timePeriod && { period: timePeriod }),
			...(dateRange && { from: dateRange.from, to: dateRange.to }),
			...(selectedPin && { pinned_drops: selectedPin }),
			...(selectedCategory && { node_id: selectedCategory }),
		};
	}
	const api = createApiInstance();
	const response = await api.get(
		useRuntimeConfig().public.apiBaseUrl + '/v1/drops',
		{
			params: body,
		},
	);
	if (response.status === 404) {
		return [];
	}
	if (response.status >= 200 && response.status < 300) {
		try {
			return validator.verify(response.data);
		} catch (error) {
			console.log('Failed to validate drops', error);
			throw new Error('Failed to validate drops');
		}
	} else {
		throw new Error('Failed to fetch drops');
	}
};

export const useDrops = (opts: {
	filters: Ref<DynamicInputData | StaticInputData>;
	enabled: Ref<boolean>;
}) => {
	const filterString = computed(() => {
		return JSON.stringify(opts.filters.value);
	});

	const { data, isFetching, status, error, suspense, isLoading } = useQuery({
		queryKey: ['drops', filterString],
		queryFn: () => fetchDrops(opts.filters.value),
		enabled: opts.enabled,
		staleTime: 25 * 1000 * 60,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		gcTime: 25 * 1000 * 60,
		retry: false,
	});

	return {
		data,
		isLoading,
		dropsLoading: isFetching,
		status,
		error,
		waitForDrops: suspense,
	};
};
